import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import knowledgeApi from '@/lib/knowledge-api'

// ── Types ────────────────────────────────────────────────────────────

export interface GenClairRun {
  id: string
  name: string
  description: string
  source_type: 'ragflow_dataset' | 'file_upload'
  source_file_name: string
  knowledge_base_id: string | null
  knowledge_base_name: string | null
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
  progress: number
  total_chunks_processed: number
  intents_discovered: number
  error_log: string
  last_completed_step: string
  triggered_by: string | null
  created_at: string
  completed_at: string | null
}

export interface GenClairRunDetail extends GenClairRun {
  intents: {
    id: string
    name: string
    description: string
    document_count: number
    approved: string
    tag_count: number
    entity_type_count: number
  }[]
}

export interface GenClairIntent {
  id: string
  name: string
  description: string
  document_count: number
  cluster_id: number
  approved: 'pending' | 'approved' | 'rejected'
  tags: { id: string; name: string; description: string; approved: string }[]
  entity_types: { id: string; name: string; description: string; example: string; approved: string }[]
}

export interface GenClairResult {
  run_id: string
  status: string
  intents_discovered: number
  intents: GenClairIntent[]
}

export interface GenClairGraphData {
  nodes: { name: string; description: string; example: string; intent_name: string; approved: string }[]
  edges: { source: string; target: string; weight: number }[]
}

export interface PaginatedGenClairResponse {
  items: GenClairRun[]
  total: number
  page: number
  page_size: number
}

// ── List all runs (paginated) ───────────────────────────────────────

export function useGenClairRuns(params?: { page?: number; pageSize?: number; search?: string }) {
  const { page = 1, pageSize = 10, search = '' } = params || {}
  return useQuery<PaginatedGenClairResponse>({
    queryKey: ['genclair-runs', page, pageSize, search],
    queryFn: async () => {
      const res = await knowledgeApi.get('/knowledge/api/genclair/runs/', {
        params: { page, page_size: pageSize, search },
      })
      return res.data
    },
  })
}

// ── Bulk delete runs ────────────────────────────────────────────────

export function useBulkDeleteGenClairRuns() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const res = await knowledgeApi.post('/knowledge/api/genclair/runs/bulk-delete/', { ids })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['genclair-runs'] })
    },
  })
}

// ── Run detail ───────────────────────────────────────────────────────

export function useGenClairRunDetail(runId: string | undefined) {
  return useQuery<GenClairRunDetail>({
    queryKey: ['genclair-run', runId],
    queryFn: async () => {
      const res = await knowledgeApi.get(`/knowledge/api/genclair/runs/${runId}/`)
      return res.data
    },
    enabled: !!runId,
  })
}

// ── Poll run status ──────────────────────────────────────────────────

export function useGenClairRunStatus(runId: string | undefined) {
  return useQuery<GenClairRun>({
    queryKey: ['genclair-run-status', runId],
    queryFn: async () => {
      const res = await knowledgeApi.get(`/knowledge/api/genclair/runs/${runId}/status/`)
      return res.data
    },
    enabled: !!runId,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return 5000
      if (data.status === 'completed' || data.status === 'failed') return false
      return 3000
    },
    refetchIntervalInBackground: true,
  })
}

// ── Full result (intents, tags, entities) ────────────────────────────

export function useGenClairRunResult(runId: string | undefined, enabled: boolean = true) {
  return useQuery<GenClairResult>({
    queryKey: ['genclair-run-result', runId],
    queryFn: async () => {
      const res = await knowledgeApi.get(`/knowledge/api/genclair/runs/${runId}/result/`)
      return res.data
    },
    enabled: !!runId && enabled,
  })
}

// ── Graph data ───────────────────────────────────────────────────────

export function useGenClairRunGraph(runId: string | undefined, enabled: boolean = true) {
  return useQuery<GenClairGraphData>({
    queryKey: ['genclair-run-graph', runId],
    queryFn: async () => {
      const res = await knowledgeApi.get(`/knowledge/api/genclair/runs/${runId}/graph/`)
      return res.data
    },
    enabled: !!runId && enabled,
  })
}

// ── Create run ───────────────────────────────────────────────────────

interface CreateRunParams {
  name: string
  description?: string
  source_type: 'ragflow_dataset' | 'file_upload'
  knowledge_base_id?: string
}

export function useCreateGenClairRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (params: CreateRunParams) => {
      const res = await knowledgeApi.post('/knowledge/api/genclair/runs/', params)
      return res.data as GenClairRun
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['genclair-runs'] })
    },
  })
}

// ── Upload file to run ───────────────────────────────────────────────

interface UploadFileParams {
  runId: string
  file: File
  strategy?: string
  params?: Record<string, unknown>
}

export function useUploadGenClairFile() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ runId, file, strategy = 'paragraph', params = {} }: UploadFileParams) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('strategy', strategy)
      formData.append('params', JSON.stringify(params))
      const res = await knowledgeApi.post(
        `/knowledge/api/genclair/runs/${runId}/upload/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return res.data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['genclair-run', variables.runId] })
      queryClient.invalidateQueries({ queryKey: ['genclair-run-status', variables.runId] })
      queryClient.invalidateQueries({ queryKey: ['genclair-runs'] })
    },
  })
}

// ── Retry failed run ─────────────────────────────────────────────────

export function useRetryGenClairRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ runId, mode = 'resume' }: { runId: string; mode?: 'resume' | 'restart' }) => {
      const res = await knowledgeApi.post(
        `/knowledge/api/genclair/runs/${runId}/retry/`,
        { mode },
      )
      return res.data as GenClairRun
    },
    onSuccess: (_data, { runId }) => {
      queryClient.invalidateQueries({ queryKey: ['genclair-run-status', runId] })
      queryClient.invalidateQueries({ queryKey: ['genclair-run', runId] })
      queryClient.invalidateQueries({ queryKey: ['genclair-runs'] })
    },
  })
}

// ── Cancel a running/pending run ─────────────────────────────────────

export function useCancelGenClairRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (runId: string) => {
      const res = await knowledgeApi.post(`/knowledge/api/genclair/runs/${runId}/cancel/`)
      return res.data as GenClairRun
    },
    onSuccess: (_data, runId) => {
      queryClient.invalidateQueries({ queryKey: ['genclair-run-status', runId] })
      queryClient.invalidateQueries({ queryKey: ['genclair-run', runId] })
      queryClient.invalidateQueries({ queryKey: ['genclair-runs'] })
    },
  })
}

// ── Delete run ───────────────────────────────────────────────────────

export function useDeleteGenClairRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (runId: string) => {
      return knowledgeApi.delete(`/knowledge/api/genclair/runs/${runId}/delete/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['genclair-runs'] })
    },
  })
}

// ── Export run ────────────────────────────────────────────────────────

export async function exportGenClairRun(runId: string, format: 'json' | 'csv' = 'json') {
  const res = await knowledgeApi.get(
    `/knowledge/api/genclair/runs/${runId}/export/?format=${format}`,
    { responseType: 'blob' }
  )
  const blob = new Blob([res.data])
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `taxonomy_export.${format}`
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}
